import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useRef, useState, useEffect } from "react";
import { Loader2, QrCode, RefreshCw, Baby, User, Users, Shield, BadgePlus, Info, CheckCircle, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useConnectionStatus } from "@/hooks/useConnectionStatus";
import { useAnalytics } from "@/lib/analytics";
import { AnalyticsEvents, AnalyticsParams } from "@/lib/analytics";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";

const ageRatingOptions = [
  { value: "4+", label: "4+", icon: Baby },
  { value: "9+", label: "9+", icon: User },
  { value: "12+", label: "12+", icon: Users },
  { value: "17+", label: "17+", icon: Shield },
  { value: "Restricted", label: "Restricted", icon: BadgePlus },
];

interface CreateConnectionModalProps {
  isOpen: boolean;
  onClose: (success: boolean) => void;
  onSubmit: (name: string, ageRating: string) => Promise<string | undefined>;
  onReconnect?: (id: string) => Promise<string | undefined>;
  connectionId?: string | null;
  connectionName?: string;
  showReconnectMode?: boolean;
}

export function CreateConnectionModal({
  isOpen,
  onClose,
  onSubmit,
  onReconnect,
  connectionId = null,
  connectionName = '',
  showReconnectMode = false,
}: CreateConnectionModalProps) {
  const [name, setName] = useState("");
  const [ageRating, setAgeRating] = useState<string>("12+");
  const [specialNeeds, setSpecialNeeds] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeConnectionId, setActiveConnectionId] = useState<string | null>(null);
  const [isAgeRatingDialogOpen, setIsAgeRatingDialogOpen] = useState(false);
  const [isManuallyReconnecting, setIsManuallyReconnecting] = useState(false);
  const [hasShownSuccessToast, setHasShownSuccessToast] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation();
  
  const { qrCode, status, error: connectionError, isLoading } = useConnectionStatus(
    activeConnectionId, 
    isOpen
  );

  useEffect(() => {
    if (activeConnectionId && isOpen) {
      console.log(`Connection status update - QR: ${!!qrCode}, Status: ${status}, Error: ${connectionError}, Loading: ${isLoading}`);
    }
  }, [qrCode, status, connectionError, isLoading, activeConnectionId, isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (showReconnectMode && connectionId) {
        handleInitialReconnect(connectionId, connectionName || "");
      } else {
        setName("");
        setAgeRating("12+");
        setSpecialNeeds(false);
        setError(null);
        setActiveConnectionId(null);
        setIsManuallyReconnecting(false);
        setHasShownSuccessToast(false);
      }
    } else {
      setIsManuallyReconnecting(false);
      setHasShownSuccessToast(false);
    }
  }, [isOpen, showReconnectMode, connectionId, connectionName]);

  const handleInitialReconnect = async (id: string, name: string) => {
    setName(name);
    setAgeRating("12+");
    setSpecialNeeds(false);
    setError(null);
    setIsManuallyReconnecting(true);
    
    try {
      if (onReconnect) {
        trackEvent(AnalyticsEvents.CONNECTION_RECONNECT_ATTEMPT, {
          [AnalyticsParams.CONNECTION_ID]: id,
        });
        
        const newConnectionId = await onReconnect(id);
        
        if (newConnectionId) {
          setActiveConnectionId(newConnectionId);
          console.log(`Reconnection initiated, new connection ID: ${newConnectionId}`);
          
          toast({
            title: "Reconnecting",
            description: "Attempting to reconnect to WhatsApp...",
          });
          
          trackEvent(AnalyticsEvents.CONNECTION_RECONNECT_SUCCESS, {
            [AnalyticsParams.CONNECTION_ID]: id,
            [AnalyticsParams.NEW_CONNECTION_ID]: newConnectionId,
          });
        } else {
          throw new Error("Failed to reconnect. Please try again.");
        }
      }
    } catch (err) {
      console.error("Initial reconnect error:", err);
      setError("Failed to reconnect. Please try again.");
      
      trackEvent(AnalyticsEvents.CONNECTION_FAILED, {
        [AnalyticsParams.CONNECTION_ID]: id,
        [AnalyticsParams.ERROR_MESSAGE]: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsManuallyReconnecting(false);
    }
  };

  useEffect(() => {
    if (connectionError && !isManuallyReconnecting) {
      console.error("Connection error from Firestore:", connectionError);
      setError(connectionError);
      
      trackEvent(AnalyticsEvents.CONNECTION_FAILED, {
        [AnalyticsParams.CONNECTION_ID]: activeConnectionId || '',
        [AnalyticsParams.ERROR_MESSAGE]: connectionError,
      });
    }
  }, [connectionError, activeConnectionId, trackEvent, isManuallyReconnecting]);

  useEffect(() => {
    if (status === "connected" && !hasShownSuccessToast && isOpen && activeConnectionId) {
      setHasShownSuccessToast(true);
      handleConnectionSuccess();
      
      
      trackEvent(AnalyticsEvents.CONNECTION_PAIRED, {
        [AnalyticsParams.CONNECTION_ID]: activeConnectionId || '',
        [AnalyticsParams.SUCCESS]: true,
      });
    }
  }, [status, activeConnectionId, trackEvent, hasShownSuccessToast, isOpen]);

  const handleConnectionSuccess = () => {
    toast({
      title: "Connection successful",
      description: "Your WhatsApp device has been connected",
    });
    
    // Add a 3-second delay so user can see the success modal
    setTimeout(() => {
      onClose(true);
    }, 3000);
  };

  const handleManualReconnect = async () => {
    if (!activeConnectionId) return;
    
    try {
      setError(null);
      setIsManuallyReconnecting(true);
      
      trackEvent(AnalyticsEvents.CONNECTION_RECONNECT_ATTEMPT, {
        [AnalyticsParams.CONNECTION_ID]: activeConnectionId,
      });
      
      if (onReconnect) {
        const newConnectionId = await onReconnect(activeConnectionId);
        
        if (newConnectionId) {
          setActiveConnectionId(newConnectionId);
          
          toast({
            title: "Reconnecting",
            description: "Attempting to reconnect to WhatsApp...",
          });
          
          trackEvent(AnalyticsEvents.CONNECTION_RECONNECT_SUCCESS, {
            [AnalyticsParams.CONNECTION_ID]: activeConnectionId,
            [AnalyticsParams.NEW_CONNECTION_ID]: newConnectionId,
          });
        } else {
          throw new Error("Failed to reconnect. Please try again.");
        }
      }
    } catch (err) {
      console.error("Manual reconnect error:", err);
      setError("Failed to reconnect. Please try again.");
      
      trackEvent(AnalyticsEvents.CONNECTION_FAILED, {
        [AnalyticsParams.CONNECTION_ID]: activeConnectionId,
        [AnalyticsParams.ERROR_MESSAGE]: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsManuallyReconnecting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError("Connection name cannot be empty");
      return;
    }
    
    try {
      setError(null);
      setIsSubmitting(true);
      
      const id = await onSubmit(name, ageRating);
      
      if (id) {
        console.log(`Connection created with ID: ${id}`);
        setActiveConnectionId(id);
        
        trackEvent(AnalyticsEvents.CONNECTION_CREATED, {
          [AnalyticsParams.CONNECTION_ID]: id,
          [AnalyticsParams.CONNECTION_NAME]: name,
          [AnalyticsParams.AGE_RATING]: ageRating,
        });
      } else {
        throw new Error("Failed to create connection");
      }
    } catch (err) {
      console.error("Create connection error:", err);
      setError("Failed to create connection. Please try again.");
      
      trackEvent(AnalyticsEvents.CONNECTION_FAILED, {
        [AnalyticsParams.CONNECTION_NAME]: name,
        [AnalyticsParams.AGE_RATING]: ageRating,
        [AnalyticsParams.ERROR_MESSAGE]: err instanceof Error ? err.message : 'Unknown error',
      });
      
      toast({
        title: "Error",
        description: "Failed to create connection",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose(false); // Pass false to indicate that the connection was canceled
  };

  const renderModalContent = () => {
    console.log("Rendering modal content:", {
      connectionId: activeConnectionId,
      qrCode: !!qrCode,
      qrLength: qrCode?.length || 0,
      status,
      isLoading,
      error,
      isManuallyReconnecting
    });
    
    if (isManuallyReconnecting) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <RefreshCw className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-center text-muted-foreground">
            Reconnecting to WhatsApp...
          </p>
        </div>
      );
    }
    
    if (activeConnectionId && isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-center text-muted-foreground">
            Preparing your WhatsApp connection...
          </p>
        </div>
      );
    }
    
    if (activeConnectionId) {
      if (status === "connected") {
        return (
          <div className="flex flex-col items-center justify-center py-4">
            <div className="flex flex-col items-center space-y-2 py-4 animate-scale-in">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-green-600 font-medium">Connected successfully!</p>
            </div>
          </div>
        );
      }
      
      if (error || status === "disconnected" || status === "refused") {
        return (
          <div className="flex flex-col items-center justify-center py-6 px-4">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <div className="text-sm text-muted-foreground text-left mb-6 space-y-1">
              <p className="font-medium mb-2">{t('modal.toReconnect')}</p>
              <ol className="list-decimal list-inside space-y-1 pl-2">
                {(t('modal.reconnectSteps', { returnObjects: true }) as string[]).map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>
            <Button 
              onClick={handleManualReconnect}
              className="bg-primary hover:bg-primary/90"
              disabled={isManuallyReconnecting}
            >
              {isManuallyReconnecting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Reconnecting...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reconnect
                </>
              )}
            </Button>
          </div>
        );
      }
      
      return (
        <div className="flex flex-col items-center justify-center py-4">
          {isManuallyReconnecting ? (
            <div className="flex flex-col items-center space-y-4 py-6">
              <RefreshCw className="h-12 w-12 animate-spin text-primary mb-2" />
              <p className="text-center text-muted-foreground">
                Reconnecting to WhatsApp...
              </p>
            </div>
          ) : qrCode ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 w-full">
                <p className="text-sm text-blue-800 text-center">
                  {t('helpers.useChildPhone')}
                </p>
              </div>
              <div className="bg-white p-4 rounded-md mb-4">
                <img 
                  src={qrCode} 
                  alt="WhatsApp QR Code" 
                  className="w-66 h-66"
                  onError={(e) => {
                    console.error("QR code image failed to load:", e);
                    setError("Failed to display QR code. Please try reconnecting.");
                  }}
                  onLoad={() => {
                    console.log("QR code image loaded successfully");
                  }}
                />
              </div>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                {(t('modal.qrInstructions', { returnObjects: true }) as string[]).map((instruction, index) => (
                  <li key={index}>{instruction}</li>
                ))}
              </ol>
            </>
          ) : (
            <div className="flex flex-col items-center space-y-4 py-6">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-2" />
              <p className="text-center text-muted-foreground">
                Waiting for QR code...
              </p>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <form onSubmit={handleSubmit}>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-start gap-4">
            <Label htmlFor="name" className="text-right pt-2">
              Name
            </Label>
            <div className="col-span-3 space-y-2">
              <p className="text-sm text-muted-foreground">
                {t('helpers.nameInputAbove')}
              </p>
              <Input
                id="name"
                placeholder={t('modal.placeholders.childName')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
              />
              <p className="text-sm text-muted-foreground">
                {t('helpers.nameInputBelow')}
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="age-rating" className="text-right">
              Age Rating
            </Label>
            <div className="flex items-center gap-2 col-span-3">
              <Select
                value={ageRating}
                onValueChange={setAgeRating}
              >
                <SelectTrigger id="age-rating" className="w-full">
                  <SelectValue placeholder="Select age rating" />
                </SelectTrigger>
                <SelectContent className="bg-background">
                  {ageRatingOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center">
                          <Icon className="mr-2 h-4 w-4" />
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      type="button" 
                      size="icon" 
                      variant="ghost" 
                      className="h-9 w-9"
                      onClick={() => setIsAgeRatingDialogOpen(true)}
                    >
                      <Info className="h-4 w-4" />
                      <span className="sr-only">Age Rating Information</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View age rating details</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          
          <div className="grid grid-cols-4 items-start gap-4">
              <div></div>
              <div className="col-span-3">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="special-needs"
                    checked={specialNeeds}
                    onCheckedChange={(checked) => setSpecialNeeds(checked === true)}
                    className="mt-1"
                  />
                  <Label htmlFor="special-needs" className="text-sm font-medium cursor-pointer">
                    {t('modal.specialNeedsDescription')}
                  </Label>
                </div>
              </div>
            </div>

          {error && (
            <div className="text-destructive text-sm px-4">{error}</div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90"
          >
            {isSubmitting ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </form>
    );
  };

  return (
    <>
      <Dialog 
        open={isOpen} 
        onOpenChange={(open) => {
          if (!open && !isSubmitting) {
            handleClose();
          }
        }}
      >
        <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto animate-fade-in" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>
              {isManuallyReconnecting ? "Reconnecting..." :
                !activeConnectionId ? t('modal.addChildWhatsApp') : 
               status === "connected" ? "Connection Successful" : 
               error || status === "disconnected" ? t('modal.connectionExpired') :
               t('modal.scanQR')}
            </DialogTitle>
            <DialogDescription>
              {isManuallyReconnecting ? "Attempting to reconnect to WhatsApp..." :
              !activeConnectionId ? (
                "Add a new WhatsApp connection to monitor."
              ) :
               status === "connected" ? "Your WhatsApp connection is now active." :
               error || status === "disconnected" ? t('modal.qrExpired') :
               "Scan this QR code with your child's WhatsApp mobile app."}
            </DialogDescription>
          </DialogHeader>
          
          {renderModalContent()}
        </DialogContent>
      </Dialog>

      <Dialog open={isAgeRatingDialogOpen} onOpenChange={(open) => {
        setIsAgeRatingDialogOpen(open);
      }}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Age Rating Information</DialogTitle>
            <DialogDescription>
              Select the appropriate age rating for your connection content
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-3">
            <div className="space-y-2">
              <div className="flex items-center">
                <Baby className="h-5 w-5 mr-2 text-emerald-500" />
                <h3 className="font-semibold">4+ (Very Safe Content)</h3>
              </div>
              <ul className="ml-7 list-disc text-sm text-muted-foreground space-y-1">
                <li>No sexual content, realistic violence, or hate speech.</li>
                <li>Only extremely mild fantasy/cartoon violence (like children's cartoons).</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center">
                <User className="h-5 w-5 mr-2 text-blue-500" />
                <h3 className="font-semibold">9+ (Mild Content, No Real-World Harm)</h3>
              </div>
              <ul className="ml-7 list-disc text-sm text-muted-foreground space-y-1">
                <li>Allows mild cartoon/fantasy violence, crude humor, and mild horror elements.</li>
                <li>Low tolerance for self-harm or hate speech.</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2 text-amber-500" />
                <h3 className="font-semibold">12+ (Moderate Content, Some Real-World Themes)</h3>
              </div>
              <ul className="ml-7 list-disc text-sm text-muted-foreground space-y-1">
                <li>Allows some references to drugs, alcohol, and medical treatment.</li>
                <li>Allows mild sexual content, profanity, and realistic violence.</li>
                <li>Still restricts strong hate speech and severe violence.</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center">
                <Shield className="h-5 w-5 mr-2 text-orange-500" />
                <h3 className="font-semibold">17+ (Mature Content)</h3>
              </div>
              <ul className="ml-7 list-disc text-sm text-muted-foreground space-y-1">
                <li>Allows frequent or intense mature content, gambling, sexual themes, and strong violence.</li>
                <li>Restrictions apply mainly to extreme cases (e.g., explicit sexual content or high hate speech scores).</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center">
                <BadgePlus className="h-5 w-5 mr-2 text-red-500" />
                <h3 className="font-semibold">Restricted (&gt; 0.8 Scores)</h3>
              </div>
              <ul className="ml-7 list-disc text-sm text-muted-foreground space-y-1">
                <li>Content that exceeds this threshold is highly inappropriate for public distribution.</li>
                <li>May involve explicit violence, strong hate speech, or illegal material.</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setIsAgeRatingDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
